import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ContextIcon } from '@/shared/components/ui'
import { OverflowMenu, type OverflowMenuItem } from '@/shared/components/ui/OverflowMenu'
import { DataTable, ListPageLayout, type DataTableColumn } from '@/shared/components/entities'
import { TeamModal } from '@/modules/units'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { usePermissions } from '@/modules/units/usePermissions'
import { AccountingClientModal } from '@/modules/accounting/clients/AccountingClientModal'
import { AccountingClientUploadModal } from '@/modules/accounting/clients/AccountingClientUploadModal'
import { ResetAccountingClientModal } from '@/modules/accounting/clients/ResetAccountingClientModal'
import { BulkBookkeepingModal, ClearBookkeepingModal } from '@/modules/accounting/bookkeeping'
import { listAccountingClientStatsDocuments, listAccountingClients, type AccountingClientStatsDocument } from '@/modules/accounting/clients/accountingClientsService'
import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import { hydrateDocumentsFiscalSummaries } from '@/modules/documents'
import { getAccountingDocumentMovement, hasCurrentAccountingFiscalSummary, type AccountingDocumentMovement } from '@/modules/accounting/accountingDocumentMovement'

interface BookkeepingDocumentStats {
  total: number
  booked: number
}

type ClientDocumentStats = Record<AccountingDocumentMovement, BookkeepingDocumentStats>

const emptyMovementStats = (): BookkeepingDocumentStats => ({ total: 0, booked: 0 })
const emptyDocumentStats = (): ClientDocumentStats => ({
  incoming: emptyMovementStats(),
  outgoing: emptyMovementStats(),
})

function calculateDocumentStats(
  clients: AccountingClient[],
  documents: AccountingClientStatsDocument[],
) {
  const clientByUnit = new Map(clients.map((client) => [client.client_unit_id, client]))
  const statsByParty: Record<string, ClientDocumentStats> = Object.fromEntries(
    clients.map((client) => [client.party_id, emptyDocumentStats()]),
  )

  documents.forEach((document) => {
    const client = clientByUnit.get(document.unit_id)
    if (!client || !hasCurrentAccountingFiscalSummary(document)) return
    const movement = getAccountingDocumentMovement(document, client.cnpj)
    statsByParty[client.party_id][movement].total += 1
    if (['processed', 'purged'].includes(document.status)) {
      statsByParty[client.party_id][movement].booked += 1
    }
  })

  return statsByParty
}

function UsersIcon() {
  return <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 0 0-5.36-1.86M17 20H7m10 0v-2c0-.66-.13-1.28-.36-1.86M7 20H2v-2a3 3 0 0 1 5.36-1.86M7 20v-2c0-.66-.13-1.28-.36-1.86m0 0a5 5 0 0 1 9.28 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
}

function DocumentsIcon() {
  return <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.59a1 1 0 0 1 .7.29l5.42 5.42a1 1 0 0 1 .29.7V19a2 2 0 0 1-2 2Z" /></svg>
}

function BookkeepingIcon({ loading = false }: { loading?: boolean }) {
  if (loading) {
    return <span className="block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
  }
  return <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12.75 11.25 15 15 9.75M7.5 3.75h9A2.25 2.25 0 0 1 18.75 6v12A2.25 2.25 0 0 1 16.5 20.25h-9A2.25 2.25 0 0 1 5.25 18V6A2.25 2.25 0 0 1 7.5 3.75Zm1.5 0v-1.5m6 1.5v-1.5" /></svg>
}

function ClearBookkeepingIcon() {
  return <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m14.25 5.25 4.5 4.5m-12 9H18m-13.5-3 9.75-9.75a2.12 2.12 0 0 1 3 3L9 17.25H6.75L4.5 15Z" /></svg>
}

function MemoryIcon() {
  return <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 3.75h6M9 20.25h6M7.5 6.75h9a2.25 2.25 0 0 1 2.25 2.25v6A2.25 2.25 0 0 1 16.5 17.25h-9A2.25 2.25 0 0 1 5.25 15V9A2.25 2.25 0 0 1 7.5 6.75Zm1.5 3h6m-6 3h4.5" /></svg>
}

function ResetClientIcon() {
  return <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3M4.5 4.5v5.25h5.25M9 10.5l6 6m0-6-6 6" /></svg>
}

export function AccountingDashboard() {
  const navigate = useNavigate()
  const { t, locale } = useTranslation()
  const { currentUnit, userRole } = useUnit()
  const permissions = usePermissions(userRole)
  const [clients, setClients] = useState<AccountingClient[]>([])
  const [documentStats, setDocumentStats] = useState<Record<string, ClientDocumentStats>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
  const [showClientModal, setShowClientModal] = useState(false)
  const [editingClient, setEditingClient] = useState<AccountingClient | null>(null)
  const [uploadClient, setUploadClient] = useState<AccountingClient | null>(null)
  const [teamClient, setTeamClient] = useState<AccountingClient | null>(null)
  const [bulkBookkeepingClient, setBulkBookkeepingClient] = useState<AccountingClient | null>(null)
  const [clearBookkeepingClient, setClearBookkeepingClient] = useState<AccountingClient | null>(null)
  const [resetClient, setResetClient] = useState<AccountingClient | null>(null)

  const loadClients = useCallback(async () => {
    if (!currentUnit?.id) return
    setLoading(true)
    setError(null)
    try {
      const loadedClients = await listAccountingClients(currentUnit.id)
      const documents = await listAccountingClientStatsDocuments(
        loadedClients.map((client) => client.client_unit_id),
      )
      setClients(loadedClients)
      setDocumentStats(calculateDocumentStats(loadedClients, documents))
      void hydrateDocumentsFiscalSummaries(documents, (documentId, summary) => {
        const document = documents.find((candidate) => candidate.id === documentId)
        if (!document) return
        document.metadata = { ...document.metadata, fiscal_summary: summary }
        setDocumentStats(calculateDocumentStats(loadedClients, documents))
      }).catch((hydrateError) => {
        console.warn('[AccountingDashboard] Background summary hydration failed:', hydrateError)
      })
    } catch (loadError: any) {
      console.error('[AccountingDashboard] Error loading clients:', loadError)
      setError(loadError?.message || t('accounting.clients.loadError'))
    } finally {
      setLoading(false)
    }
  }, [currentUnit?.id, t])

  useEffect(() => { void loadClients() }, [loadClients])

  const formatDate = (value: string | null) => value
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(value))
    : t('accounting.clients.noDocuments')

  const filteredClients = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase(locale)
    if (!query) return clients
    return clients.filter((client) => [client.name, client.legal_name, client.trade_name, client.cnpj]
      .some((value) => value?.toLocaleLowerCase(locale).includes(query)))
  }, [clients, locale, searchQuery])

  const openClient = (client: AccountingClient, movement?: AccountingDocumentMovement) => navigate(`/accounting/clients/${client.party_id}${movement ? `?movement=${movement}` : ''}`)
  const statsFor = (client: AccountingClient) => documentStats[client.party_id] || emptyDocumentStats()
  const percentage = (value: number, total: number) => total > 0 ? Math.round((value / total) * 100) : 0
  const bookedFor = (stats: ClientDocumentStats) => stats.incoming.booked + stats.outgoing.booked
  const isBookkeepingDone = (stats: ClientDocumentStats, documentCount: number) => (
    documentCount > 0 && bookedFor(stats) === documentCount
  )
  const clientActions = (client: AccountingClient) => {
    const actions: OverflowMenuItem[] = []
    if (permissions.canManageTeam) actions.push({
      label: t('accounting.clients.editRegistration'),
      icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m16.86 3.49 3.65 3.65M18.69 1.66a2.58 2.58 0 0 1 3.65 3.65L8.17 19.48 3 21l1.52-5.17L18.69 1.66Z" /></svg>,
      onClick: () => setEditingClient(client),
    })
    if (permissions.canEditEntries) actions.push({
      label: t('accounting.clients.upload.action'),
      icon: <DocumentsIcon />,
      onClick: () => setUploadClient(client),
    })
    actions.push({
      label: t('accounting.clients.fiscalMemory'),
      icon: <MemoryIcon />,
      onClick: () => navigate(`/accounting/clients/${client.party_id}/fiscal-memory`),
    })
    if (permissions.canDeleteEntries) actions.push({
      label: t('accounting.bookkeeping.clear.action'),
      icon: <ClearBookkeepingIcon />,
      danger: true,
      onClick: () => setClearBookkeepingClient(client),
    })
    if (permissions.canManageTeam) actions.push({
      label: t('accounting.clients.manageAccess'),
      icon: <UsersIcon />,
      onClick: () => setTeamClient(client),
    })
    if (permissions.canDeleteEntries) actions.push({
      label: t('accounting.clients.reset.action'),
      icon: <ResetClientIcon />,
      danger: true,
      onClick: () => setResetClient(client),
    })
    return actions
  }
  const bulkBookkeepingButton = (client: AccountingClient) => {
    const stats = statsFor(client)
    const pending = (stats.incoming.total - stats.incoming.booked)
      + (stats.outgoing.total - stats.outgoing.booked)
    const active = bulkBookkeepingClient?.party_id === client.party_id
    return (
      <button
        type="button"
        disabled={pending === 0 || active}
        onClick={(event) => {
          event.stopPropagation()
          setBulkBookkeepingClient(client)
        }}
        title={t('accounting.bookkeeping.bulk.action')}
        aria-label={t('accounting.bookkeeping.bulk.action')}
        className="rounded-lg p-2 text-emerald-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:text-slate-300 dark:text-emerald-400 dark:hover:bg-emerald-950/30 dark:hover:text-emerald-300 dark:disabled:text-slate-700"
      >
        <BookkeepingIcon />
      </button>
    )
  }

  const columns: DataTableColumn<AccountingClient>[] = [
    {
      key: 'name',
      label: t('accounting.clients.columns.client'),
      render: (client) => <div><p className="font-semibold text-slate-900 dark:text-white">{client.name}</p><p className="text-xs text-slate-500">{client.cnpj || t('accounting.clients.noCnpj')}</p></div>,
    },
    { key: 'documents', label: t('accounting.clients.documents'), align: 'center', render: (client) => client.document_count },
    { key: 'incoming', label: t('accounting.clients.documentMovements.incoming'), align: 'center', render: (client) => { const stats = statsFor(client).incoming; return `${stats.total} · ${percentage(stats.booked, stats.total)}% ${t('accounting.clients.documentStats.booked').toLocaleLowerCase(locale)}` } },
    { key: 'outgoing', label: t('accounting.clients.documentMovements.outgoing'), align: 'center', render: (client) => { const stats = statsFor(client).outgoing; return `${stats.total} · ${percentage(stats.booked, stats.total)}% ${t('accounting.clients.documentStats.booked').toLocaleLowerCase(locale)}` } },
    { key: 'last_document', label: t('accounting.clients.lastDocument'), render: (client) => formatDate(client.last_document_at) },
    {
      key: 'status',
      label: t('accounting.clients.columns.status'),
      render: (client) => { const done = isBookkeepingDone(statsFor(client), client.document_count); return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${done ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>{t(`accounting.clients.status.${done ? 'bookkeepingDone' : 'bookkeepingPending'}`)}</span> },
    },
    {
      key: 'actions',
      label: t('accounting.clients.columns.actions'),
      align: 'right',
      render: (client) => <div className="flex items-center justify-end gap-1" onClick={(event) => event.stopPropagation()}>{permissions.canEditEntries && bulkBookkeepingButton(client)}<OverflowMenu items={clientActions(client)} /></div>,
    },
  ]

  return (
    <>
      <ListPageLayout
        title={t('accounting.dashboard.title')}
        description={`${currentUnit?.name || ''} · ${t('accounting.clients.total', { count: String(clients.length) })}`}
        icon={<ContextIcon context="accounting" className="h-7 w-7 text-emerald-700 dark:text-emerald-400" />}
        onNew={permissions.canManageTeam ? () => setShowClientModal(true) : undefined}
        newButtonLabel={t('accounting.clients.new')}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('accounting.clients.search')}
        clearSearchLabel={t('actions.filter')}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onMobileViewOverride={setViewMode}
        showViewToggle
        viewCardLabel={t('accounting.clients.views.cards')}
        viewTableLabel={t('accounting.clients.views.table')}
        loading={loading}
        error={error}
        stickyToolbar={!loading ? <p className="px-1 text-sm text-slate-600 dark:text-slate-400">{t('accounting.clients.showing', { shown: String(filteredClients.length), total: String(clients.length) })}</p> : undefined}
      >
        {!loading && filteredClients.length === 0 ? (
          <section className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">{searchQuery ? t('accounting.clients.noSearchResults') : t('accounting.dashboard.emptyTitle')}</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500 dark:text-slate-400">{searchQuery ? t('accounting.clients.tryAnotherSearch') : t('accounting.dashboard.emptyDescription')}</p>
          </section>
        ) : viewMode === 'table' ? (
          <DataTable data={filteredClients} columns={columns} keyExtractor={(client) => client.party_id} onRowClick={openClient} />
        ) : (
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filteredClients.map((client) => {
              const stats = statsFor(client)
              return (
              <article
                key={client.party_id}
                role="button"
                tabIndex={0}
                onClick={() => openClient(client)}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) openClient(client)
                }}
                className="cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500/40 dark:border-slate-700 dark:bg-slate-900"
              >
                <div className="block w-full border-b border-slate-100 p-5 text-left dark:border-slate-800">
                  <div className="flex items-start justify-between gap-3"><div className="min-w-0"><h2 className="truncate text-lg font-semibold text-slate-900 dark:text-white">{client.name}</h2><p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">{client.cnpj || t('accounting.clients.noCnpj')}</p></div><div className="flex shrink-0 items-center gap-1"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${isBookkeepingDone(stats, client.document_count) ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>{t(`accounting.clients.status.${isBookkeepingDone(stats, client.document_count) ? 'bookkeepingDone' : 'bookkeepingPending'}`)}</span>{permissions.canEditEntries && bulkBookkeepingButton(client)}<OverflowMenu items={clientActions(client)} /></div></div>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between"><div className="flex items-center gap-2 text-slate-500 dark:text-slate-400"><DocumentsIcon /><span className="text-xs font-semibold uppercase tracking-wide">{t('accounting.clients.documents')}</span></div><span className="text-2xl font-semibold text-slate-900 dark:text-white">{client.document_count}</span></div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {(['incoming', 'outgoing'] as const).map((movement) => {
                      const movementStats = stats[movement]
                      const bookedPercentage = percentage(movementStats.booked, movementStats.total)
                      const pending = movementStats.total - movementStats.booked
                      const pendingPercentage = percentage(pending, movementStats.total)
                      return <button key={movement} type="button" onClick={(event) => { event.stopPropagation(); openClient(client, movement) }} className="rounded-xl border border-slate-200 p-3 text-left transition-colors hover:border-emerald-400 hover:bg-emerald-50/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 dark:border-slate-700 dark:hover:border-emerald-600 dark:hover:bg-emerald-950/20">
                        <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t(`accounting.clients.documentMovements.${movement}`)}</span><span className="text-xl font-semibold text-slate-900 dark:text-white">{movementStats.total}</span></div>
                        <div className="mt-3 flex h-1.5 overflow-hidden rounded-full bg-amber-400/80">{bookedPercentage > 0 && <span className="bg-emerald-500" style={{ width: `${bookedPercentage}%` }} />}</div>
                        <dl className="mt-3 space-y-1 text-xs"><div className="flex justify-between gap-2"><dt className="text-emerald-700 dark:text-emerald-300">{t('accounting.clients.documentStats.booked')}</dt><dd className="font-semibold text-slate-800 dark:text-slate-200">{movementStats.booked} · {bookedPercentage}%</dd></div><div className="flex justify-between gap-2"><dt className="text-amber-700 dark:text-amber-300">{t('accounting.clients.documentStats.notBooked')}</dt><dd className="font-semibold text-slate-800 dark:text-slate-200">{pending} · {pendingPercentage}%</dd></div></dl>
                      </button>
                    })}
                  </div>
                </div>
                <div className="border-t border-slate-100 px-5 py-3 text-xs dark:border-slate-800"><span className="text-slate-500 dark:text-slate-400">{t('accounting.clients.lastDocument')}: {formatDate(client.last_document_at)}</span></div>
              </article>
              )
            })}
          </section>
        )}
      </ListPageLayout>

      {currentUnit?.id && <AccountingClientModal officeUnitId={currentUnit.id} client={editingClient} isOpen={showClientModal || Boolean(editingClient)} onClose={() => { setShowClientModal(false); setEditingClient(null) }} onSaved={() => void loadClients()} />}
      <AccountingClientUploadModal client={uploadClient} isOpen={Boolean(uploadClient)} onClose={() => setUploadClient(null)} onUploaded={() => void loadClients()} />
      <TeamModal unitId={teamClient?.client_unit_id || ''} isOpen={Boolean(teamClient)} onClose={() => { setTeamClient(null); void loadClients() }} />
      {currentUnit?.id && <BulkBookkeepingModal officeUnitId={currentUnit.id} client={bulkBookkeepingClient} isOpen={Boolean(bulkBookkeepingClient)} onClose={() => setBulkBookkeepingClient(null)} onSaved={() => void loadClients()} />}
      <ClearBookkeepingModal client={clearBookkeepingClient} isOpen={Boolean(clearBookkeepingClient)} onClose={() => setClearBookkeepingClient(null)} onCleared={() => void loadClients()} />
      {currentUnit?.id && <ResetAccountingClientModal officeUnitId={currentUnit.id} client={resetClient} isOpen={Boolean(resetClient)} onClose={() => setResetClient(null)} onReset={() => void loadClients()} />}
    </>
  )
}
